// 推薦收益提領 — 申請對話框（2026-06-07）
// 金額 + 補件（國家 / 手機 / PayPal Email），沿用 CreatorRegistrationDialog 的國家 Autocomplete。
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
  InputAdornment,
  Alert,
  Divider,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { country_tel } from "../../../../api/authApi";
import { createReferralWithdrawal } from "../../../../api/userApi";
import { COLORS } from "theme/tokens";

const ORANGE = COLORS.main;

export default function WithdrawalDialog({ open, onClose, onSuccess, eligibility, L = {} }) {
  const t = (k, fb) => L[k] || fb;
  const minAmount = Number(eligibility?.minAmount || 0);
  const available = Number(eligibility?.availableUsd || 0);
  const profile = eligibility?.profile || {};

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    country: "",
    phoneCountry: "",
    phoneNumber: "",
    paypalEmail: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // 開啟時初始化（帶入預填資料）
  useEffect(() => {
    if (!open) return;
    setError("");
    setForm({
      amount: "",
      country: profile.country || "",
      phoneCountry: profile.phoneCountry || "",
      phoneNumber: profile.phoneNumber || "",
      paypalEmail: profile.paypalEmail || "",
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // 載入國家代碼
  useEffect(() => {
    if (!open) return;
    country_tel()
      .then((res) => setCountries(res.data || []))
      .catch(() => setCountries([]));
  }, [open]);

  // 國家載入後對應預填值
  useEffect(() => {
    if (countries.length && form.country) {
      const found = countries.find((c) => c.country === form.country);
      if (found) setSelectedCountry(found);
    }
  }, [countries]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const errMap = {
    BELOW_MIN: t("WdErrBelowMin", "低於最低提領金額"),
    INSUFFICIENT_BALANCE: t("WdErrInsufficient", "可提領餘額不足"),
    INVALID_PAYPAL: t("WdErrInvalidPaypal", "請輸入有效的 PayPal Email"),
    MISSING_COUNTRY: t("WdErrMissing", "請填寫所有必填欄位"),
    MISSING_PHONE: t("WdErrMissing", "請填寫所有必填欄位"),
    INVALID_AMOUNT: t("WdErrMissing", "請填寫所有必填欄位"),
  };

  const handleSubmit = async () => {
    setError("");
    const amount = Number(form.amount);
    if (!amount || amount <= 0 || !form.country || !form.phoneNumber || !form.paypalEmail) {
      setError(t("WdErrMissing", "請填寫所有必填欄位"));
      return;
    }
    if (minAmount > 0 && amount < minAmount) {
      setError(errMap.BELOW_MIN);
      return;
    }
    if (amount > available) {
      setError(errMap.INSUFFICIENT_BALANCE);
      return;
    }
    setSubmitting(true);
    try {
      await createReferralWithdrawal({
        amount,
        country: form.country,
        phoneCountry: form.phoneCountry,
        phoneNumber: form.phoneNumber,
        paypalEmail: form.paypalEmail,
      });
      onSuccess?.();
    } catch (e) {
      const code = e?.response?.data?.code;
      setError(errMap[code] || e?.response?.data?.message || t("WdSubmitFailed", "申請失敗，請稍後再試"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose?.()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
        {t("WdDialogTitle", "申請提領收益")}
      </DialogTitle>
      <Divider sx={{ m: 0 }} />
      <DialogContent>
        {/* 可提領餘額 */}
        <Box
          sx={{
            mt: 1,
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: "#fff7f1",
            border: "1px solid #ffe2cf",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MDTypography sx={{ fontSize: 14, color: COLORS.grayDark }}>
            {t("WdAvailable", "可提領餘額")}
          </MDTypography>
          <MDTypography sx={{ fontSize: 24, fontWeight: 700, color: ORANGE }}>
            ${available.toFixed(2)}
          </MDTypography>
        </Box>

        <Box display="flex" flexDirection="column" gap={2.5}>
          {/* 金額 */}
          <TextField
            fullWidth
            type="number"
            label={t("WdAmount", "提領金額 (USD)")}
            placeholder={t("WdAmountPlaceholder", "請輸入提領金額")}
            value={form.amount}
            onChange={(e) => setField("amount", e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <MDButton
                    size="small"
                    variant="text"
                    onClick={() => setField("amount", String(available))}
                    sx={{ color: `${ORANGE} !important`, minWidth: "auto", px: 1 }}
                  >
                    MAX
                  </MDButton>
                </InputAdornment>
              ),
            }}
            helperText={
              minAmount > 0
                ? t("WdMinHint", "最低提領金額為 {{min}} USD").replace("{{min}}", minAmount)
                : ""
            }
          />

          <MDTypography sx={{ fontSize: 13, color: COLORS.grayDark, mt: -1 }}>
            {t("WdSupplementHint", "請補齊收款資訊以便撥款")}
          </MDTypography>

          {/* 國家 */}
          <Autocomplete
            options={countries}
            value={selectedCountry}
            onChange={(e, v) => {
              setSelectedCountry(v);
              setField("country", v?.country || "");
              setField("phoneCountry", v?.phoneNumber ? `+${v.phoneNumber}` : form.phoneCountry);
            }}
            getOptionLabel={(o) =>
              typeof o === "object" && o?.country
                ? `${o.country} ${o.phoneNumber ? "(+" + o.phoneNumber + ")" : ""}`
                : ""
            }
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("WdCountry", "國家")}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* 手機 */}
          <TextField
            fullWidth
            label={t("WdPhone", "手機號碼")}
            value={form.phoneNumber}
            onChange={(e) => setField("phoneNumber", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* PayPal */}
          <TextField
            fullWidth
            type="email"
            label={t("WdPayPal", "PayPal 收款 Email")}
            placeholder={t("WdPayPalPlaceholder", "用於收款的 PayPal Email")}
            value={form.paypalEmail}
            onChange={(e) => setField("paypalEmail", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <MDTypography sx={{ fontSize: 12, color: COLORS.grayMedian, lineHeight: 1.6 }}>
            {t(
              "WdNote",
              "提領申請送出後將進入審核流程（收益審核中 → 收益發放中 → 收益完成發放），可在「提領紀錄」查看進度。"
            )}
          </MDTypography>

          {error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={submitting}>
          {t("WdCancelBtn", "取消")}
        </MDButton>
        <MDButton
          variant="gradient"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "..." : t("WdSubmit", "送出申請")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
